import { AnimatePresence, motion } from 'motion/react'
import { useState, type ReactNode } from 'react'

export type QuestCategory =
  | 'survival'
  | 'vehicle'
  | 'gateway'
  | 'cooking'
  | 'hideout'
  | 'utrecht'

const CATEGORY_STYLES: Record<QuestCategory, { badge: string }> = {
  survival: {
    badge: 'border-amber-700/45 text-amber-500',
  },
  vehicle: {
    badge: 'border-emerald-700/45 text-emerald-400',
  },
  gateway: {
    badge: 'border-sky-700/45 text-sky-400',
  },
  cooking: {
    badge: 'border-lime-700/45 text-lime-400',
  },
  hideout: {
    badge: 'border-orange-700/45 text-orange-400',
  },
  utrecht: {
    badge: 'border-violet-700/45 text-violet-300',
  },
}

type QuestCollapsibleCardProps = {
  category: QuestCategory
  badgeLabel: string
  title: string
  completed?: boolean
  collapsedHint?: string
  defaultExpanded?: boolean
  children: ReactNode
}

export function QuestCollapsibleCard({
  category,
  badgeLabel,
  title,
  completed = false,
  collapsedHint,
  defaultExpanded = true,
  children,
}: QuestCollapsibleCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const styles = CATEGORY_STYLES[category]

  return (
    <div className="rounded-2xl border border-zinc-800/90 bg-zinc-900/95 shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 rounded-2xl p-4 text-left transition-colors hover:bg-zinc-800/35 min-[480px]:p-5"
      >
        <div className="min-w-0 flex-1">
          <span
            className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${styles.badge}`}
          >
            {badgeLabel}
          </span>
          <div className="mt-1.5 flex items-center gap-2">
            <h3
              className={`text-lg font-bold leading-snug tracking-tight ${
                completed ? 'text-zinc-500 line-through' : 'text-zinc-50'
              }`}
            >
              {title}
            </h3>
            {completed && !expanded && (
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-widest text-emerald-500">
                Completed
              </span>
            )}
          </div>
          <AnimatePresence initial={false} mode="popLayout">
            {!expanded && !completed && collapsedHint ? (
              <motion.p
                key="hint"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.18 }}
                className="mt-1 text-xs leading-relaxed text-zinc-500"
              >
                {collapsedHint}
              </motion.p>
            ) : null}
          </AnimatePresence>
        </div>
        <motion.span
          aria-hidden
          className="mt-1 shrink-0 text-zinc-500"
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        >
          <ChevronIcon />
        </motion.span>
      </button>

      <motion.div
        initial={false}
        animate={{
          height: expanded ? 'auto' : 0,
          opacity: expanded ? 1 : 0,
        }}
        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        className="overflow-hidden px-4 min-[480px]:px-5"
      >
        <div className="border-t border-zinc-800/80 pb-4 pt-4">{children}</div>
      </motion.div>
    </div>
  )
}

function ChevronIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M5 7.5l5 5 5-5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

type QuestTaskCheckboxProps = {
  checked: boolean
  active: boolean
  label: ReactNode
}

export function QuestTaskRow({ checked, active, label }: QuestTaskCheckboxProps) {
  return (
    <div
      className={`flex gap-3 rounded-lg py-2 pr-1 ${
        active ? 'bg-amber-950/20 ring-1 ring-amber-800/35 -mx-2 px-2' : ''
      }`}
    >
      <span
        className={`mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded border-[1.5px] transition-colors ${
          checked
            ? 'border-emerald-600 bg-emerald-600 text-white'
            : active
              ? 'border-zinc-500 bg-zinc-950'
              : 'border-zinc-600 bg-zinc-950/80'
        }`}
      >
        {checked ? (
          <svg
            className="size-2.5"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2.5 6l2.5 2.5 4.5-5.5" />
          </svg>
        ) : null}
      </span>
      <span
        className={`min-w-0 flex-1 text-sm leading-snug ${
          checked ? 'text-zinc-500 line-through' : active ? 'text-zinc-100' : 'text-zinc-400'
        }`}
      >
        {label}
      </span>
    </div>
  )
}

type GatewayObjectiveRowProps = {
  label: string
  current: number
  need: number
  done: boolean
}

export function GatewayObjectiveRow({
  label,
  current,
  need,
  done,
}: GatewayObjectiveRowProps) {
  const pct = need > 0 ? Math.min(100, (current / need) * 100) : 0

  return (
    <div className={`space-y-1.5 ${done ? 'opacity-75' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <span
          className={`min-w-0 text-sm leading-snug ${
            done ? 'text-zinc-500 line-through' : 'text-zinc-300'
          }`}
        >
          {label}
        </span>
        <span
          className={`shrink-0 tabular-nums text-xs font-medium ${
            done ? 'text-emerald-500/90' : 'text-sky-400'
          }`}
        >
          {current}/{need}
        </span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-zinc-800">
        <motion.div
          className={`h-full rounded-full ${done ? 'bg-emerald-500' : 'bg-sky-500'}`}
          initial={false}
          animate={{ width: `${done ? 100 : pct}%` }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        />
      </div>
    </div>
  )
}
